import { Category, SmmCurated } from '@apis/index'
import { deletesvg, edit, outlinestore, switchOff, switchOn } from '@assets/icons'
import Column from '@components/column'
import Svg from '@components/svg'
import Table from '@components/table'
import Title from '@components/title'
import { RootState } from '@store/index'
import imageCompression from 'browser-image-compression'
import { Facebook, Instagram } from 'lucide-react'
import React, { Fragment, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Chip,
  ChipImage,
  ChipList,
  EditableCell,
  EmptyState,
  HiddenFileInput,
  PickerLabel,
  PickerSearchInput,
  PickerWrapper,
  RowEdit,
  RowThumb,
  RowThumbPlaceholder,
  ServiceIdLabel,
  ServiceNameCell,
  ServiceNameLabel,
  Spinner,
  SubmitButton,
  VariantBadge,
} from './style'

const TIKTOK_PATH = 'M16.6 5.82c-.9-.98-1.4-2.26-1.4-3.57h-3.16v13.9a3.13 3.13 0 0 1-3.13 3.02 3.13 3.13 0 0 1 0-6.26c.29 0 .57.04.83.12V9.9a6.34 6.34 0 0 0-.83-.05 6.29 6.29 0 1 0 6.29 6.29V8.63a8.16 8.16 0 0 0 4.77 1.52V6.99a4.85 4.85 0 0 1-3.37-1.17Z'

const TikTokIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d={TIKTOK_PATH} />
  </svg>
)

const getPlatformIcon = (category: string) => {
  const lower = category.toLowerCase()
  if (lower.includes('tiktok')) return <TikTokIcon />
  if (lower.includes('facebook')) return <Facebook size={20} />
  if (lower.includes('instagram')) return <Instagram size={20} />
  return null
}

const resolveImageSrc = (image?: string | null) => {
  if (!image) return ''
  return image.startsWith('http') ? image : `https://admin.tournaop.com/${image}`
}

// Mirrors the backend's normalization (categories/service.ts) so the filter
// groups all bracketed-suffix variants of a category together, e.g.
// "TikTok Video Views [ Updated ]" and "TikTok Video Views [ Super Fast
// Complete 🚀 ]" both match the "TikTok Video Views" category.
const normalizeCategory = (category?: string | null) =>
  (category || '').split('[')[0].trim().toLowerCase()

// Final NPR rate shown to users = providerUsdRate × usdToNprRate × rateMultiplier.
// usdToNprRate converts currency (update whenever the real exchange rate moves);
// rateMultiplier is a separate markup on top of that.
const GlobalRateMultiplierPanel = ({
  rateMultiplier,
  usdToNprRate,
  onSave,
}: {
  rateMultiplier: number
  usdToNprRate: number
  onSave: (values: { rateMultiplier: number; usdToNprRate: number }) => Promise<void>
}) => {
  const [multiplierInput, setMultiplierInput] = useState(String(rateMultiplier))
  const [usdToNprInput, setUsdToNprInput] = useState(String(usdToNprRate))
  const [saving, setSaving] = useState(false)

  React.useEffect(() => {
    setMultiplierInput(String(rateMultiplier))
  }, [rateMultiplier])

  React.useEffect(() => {
    setUsdToNprInput(String(usdToNprRate))
  }, [usdToNprRate])

  const handleSave = async () => {
    const trimmedMultiplier = multiplierInput.trim()
    const trimmedUsdToNpr = usdToNprInput.trim()
    const parsedMultiplier = Number(trimmedMultiplier)
    const parsedUsdToNpr = Number(trimmedUsdToNpr)
    if (
      !trimmedMultiplier ||
      Number.isNaN(parsedMultiplier) ||
      parsedMultiplier <= 0 ||
      !trimmedUsdToNpr ||
      Number.isNaN(parsedUsdToNpr) ||
      parsedUsdToNpr <= 0
    ) {
      return
    }
    setSaving(true)
    try {
      await onSave({ rateMultiplier: parsedMultiplier, usdToNprRate: parsedUsdToNpr })
    } finally {
      setSaving(false)
    }
  }

  return (
    <PickerWrapper>
      <PickerLabel>Global rate multiplier</PickerLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, marginBottom: 4, opacity: 0.7 }}>
            Rate multiplier (markup)
          </div>
          <PickerSearchInput
            type="number"
            min={0}
            step="0.01"
            style={{ width: 140 }}
            placeholder="e.g. 2 for 2x"
            value={multiplierInput}
            onChange={(e) => setMultiplierInput(e.target.value)}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, marginBottom: 4, opacity: 0.7 }}>
            USD to NPR rate
          </div>
          <PickerSearchInput
            type="number"
            min={0}
            step="0.01"
            style={{ width: 140 }}
            placeholder="e.g. 133.5"
            value={usdToNprInput}
            onChange={(e) => setUsdToNprInput(e.target.value)}
          />
        </div>
        <SubmitButton type="button" disabled={saving} onClick={handleSave} style={{ alignSelf: 'flex-end' }}>
          {saving && <Spinner />}
          {saving ? 'Saving...' : 'Save'}
        </SubmitButton>
      </div>
    </PickerWrapper>
  )
}

const ServiceImageCell = ({
  item,
  onUploadImage,
}: {
  item: { id: number; image: string | null; name: string }
  onUploadImage: (id: number, file: File) => void
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <EditableCell>
      {item.image ? (
        <RowThumb src={resolveImageSrc(item.image)} alt={item.name} />
      ) : (
        <RowThumbPlaceholder>N/A</RowThumbPlaceholder>
      )}
      <RowEdit
        onClick={(e) => {
          e.stopPropagation()
          fileInputRef.current?.click()
        }}
      >
        <Svg src={edit} />
      </RowEdit>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onUploadImage(item.id, file)
          e.target.value = ''
        }}
      />
    </EditableCell>
  )
}

const SmmServices = () => {
  const {
    items: services,
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.smmCuratedServices)
  const [page, setPage] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [categories, setCategories] = useState<
    { id: number; name: string; image?: string | null }[]
  >([])
  const [resyncingId, setResyncingId] = useState<number | null>(null)
  const [globalRateMultiplier, setGlobalRateMultiplier] = useState(1)
  const [usdToNprRate, setUsdToNprRate] = useState(1)

  const data = useMemo(() => {
    const variantCounts = new Map<string, number>()
    const sorted = [...services].sort((a, b) => {
      if (a.serviceId === b.serviceId) return a.id - b.id
      return a.serviceId < b.serviceId ? -1 : 1
    })
    return sorted
      .filter(
        (service) =>
          !selectedCategory ||
          normalizeCategory(service.category) === normalizeCategory(selectedCategory),
      )
      .map((service) => {
        const count = (variantCounts.get(service.serviceId) || 0) + 1
        variantCounts.set(service.serviceId, count)
        const totalVariants = sorted.filter((s) => s.serviceId === service.serviceId).length
        return {
          id: service.id,
          serviceId: service.serviceId,
          name: service.name,
          category: service.category,
          rate: service.rate,
          effectiveRate: service.effectiveRate ?? service.rate,
          image: service.image ?? null,
          min: service.min,
          max: service.max,
          isEnabled: service.isEnabled,
          variantIndex: count,
          variantCount: totalVariants,
        }
      })
  }, [services, selectedCategory])

  const uploadServiceImage = async (id: number, file: File) => {
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      })
      const reader = new FileReader()
      reader.onload = async (e) => {
        const result = e.target?.result as string
        const base64 = result.split(',')[1]
        const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg'
        try {
          await SmmCurated.update({
            state: 'smmCuratedServices',
            id,
            data: { image: { base64, extension } },
          })
        } catch (error) {
          console.error(error)
        }
      }
      reader.readAsDataURL(compressed)
    } catch (error) {
      console.error(error)
    }
  }

  const columns = [
    {
      title: 'Service',
      key: 'name',
      render: (item) => (
        <ServiceNameCell>
          <ServiceNameLabel>
            {item.name}
            {item.variantCount > 1 && (
              <VariantBadge>Variant {item.variantIndex}/{item.variantCount}</VariantBadge>
            )}
          </ServiceNameLabel>
          <ServiceIdLabel>
            #{item.serviceId} &middot; {item.category} &middot; qty range {item.min}-{item.max}
          </ServiceIdLabel>
        </ServiceNameCell>
      ),
    },
    {
      title: 'Image',
      key: 'image',
      render: (item) => <ServiceImageCell item={item} onUploadImage={uploadServiceImage} />,
    },
    {
      title: 'Rate (default → effective)',
      key: 'rate',
      render: (item) => (
        <div style={{ fontSize: 12 }}>
          {item.rate} &rarr; {item.effectiveRate}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'isEnabled',
      render: (item) => (
        <Svg
          src={item.isEnabled ? switchOn : switchOff}
          onClick={async () =>
            await SmmCurated.update({
              state: 'smmCuratedServices',
              id: item.id,
              data: { isEnabled: !item.isEnabled },
            })
          }
        />
      ),
    },
  ]

  const fetchServices = async () => {
    try {
      await SmmCurated.list({
        state: 'smmCuratedServices',
        params: { page, limit: 30 },
      })
    } catch (error) {
      console.error(error)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await Category.list({ params: { type: 'smm' } })
      setCategories(
        (response?.data || response || []).map((c: any) => ({
          id: c.id,
          name: c.key,
          image: c.image,
        })),
      )
    } catch (error) {
      console.error(error)
    }
  }

  const handleResync = async (id: number) => {
    setResyncingId(id)
    try {
      await Category.resyncSmmProducts({ id })
      await fetchServices()
    } catch (error) {
      console.error(error)
    } finally {
      setResyncingId(null)
    }
  }

  const fetchRateMultiplier = async () => {
    try {
      const response = await SmmCurated.getRateMultiplier()
      setGlobalRateMultiplier(Number(response?.rateMultiplier) || 1)
      setUsdToNprRate(Number(response?.usdToNprRate) || 1)
    } catch (error) {
      console.error(error)
    }
  }

  const saveGlobalRateMultiplier = async (values: {
    rateMultiplier: number
    usdToNprRate: number
  }) => {
    try {
      await SmmCurated.updateRateMultiplier({ data: values })
      setGlobalRateMultiplier(values.rateMultiplier)
      setUsdToNprRate(values.usdToNprRate)
      await fetchServices()
    } catch (error) {
      console.error(error)
    }
  }

  React.useEffect(() => {
    fetchServices()
  }, [page])

  React.useEffect(() => {
    fetchCategories()
    fetchRateMultiplier()
  }, [])

  return (
    <Fragment>
      <Column>
        <Title textone="Store Management >" texttwo="SMM Services" />

        <GlobalRateMultiplierPanel
          rateMultiplier={globalRateMultiplier}
          usdToNprRate={usdToNprRate}
          onSave={saveGlobalRateMultiplier}
        />

        {categories.length === 0 ? (
          <EmptyState>
            No SMM categories yet. Create one on the Category page (Type: SMM) — its
            products are added automatically.
          </EmptyState>
        ) : (
          <ChipList>
            <Chip
              type="button"
              $active={!selectedCategory}
              onClick={() => setSelectedCategory('')}
            >
              All categories
            </Chip>
            {categories.map((c) => (
              <Chip
                key={c.name}
                type="button"
                $active={selectedCategory === c.name}
                onClick={() =>
                  setSelectedCategory(selectedCategory === c.name ? '' : c.name)
                }
              >
                {c.image ? (
                  <ChipImage src={resolveImageSrc(c.image)} alt={c.name} />
                ) : (
                  getPlatformIcon(c.name)
                )}
                {c.name}
                <span
                  title="Re-sync products for this category"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleResync(c.id)
                  }}
                  style={{
                    marginLeft: 6,
                    opacity: resyncingId === c.id ? 0.5 : 0.7,
                    pointerEvents: resyncingId === c.id ? 'none' : 'auto',
                  }}
                >
                  {resyncingId === c.id ? '...' : '↻'}
                </span>
              </Chip>
            ))}
          </ChipList>
        )}

        <Table
          onPageChange={setPage}
          totalItems={totalItems}
          totalPages={totalPages}
          pageControlled={page}
          itemsPerPage={30}
          data={data}
          columns={columns}
          searchKeys={['name', 'category', 'serviceId']}
          text="SMM services list"
          icon={outlinestore}
          actions={[
            {
              key: 'delete',
              icon: deletesvg,
              onClick: async (item) =>
                await SmmCurated.remove({
                  id: item.id,
                  state: 'smmCuratedServices',
                }),
            },
          ]}
        />
      </Column>
    </Fragment>
  )
}

export default SmmServices

